import { isNil } from './value-tools.ts'

// Utility type to merge T and S, allowing S to add/override properties in T
type Merge<T, S> = {
  [K in keyof T | keyof S]: K extends keyof S
    ? S[K] extends object
      ? K extends keyof T
        ? T[K] extends object
          ? Merge<T[K], S[K]>
          : S[K]
        : S[K]
      : S[K]
    : K extends keyof T
      ? T[K]
      : never
}

export function customMerge<T extends object, S extends object>(
  target: T,
  source: S,
): Merge<T, S> {
  for (const key of Object.keys(source)) {
    const sourceValue = source[key as keyof S]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const targetValue = (target as any)[key]

    if (sourceValue !== undefined && sourceValue !== null) {
      if (
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue) &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue)
      ) {
        customMerge(targetValue as object, sourceValue as object)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(target as any)[key] = sourceValue
      }
    }
  }
  return target as Merge<T, S>
}

export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>
  for (const key of keys) {
    if (Object.hasOwn(obj, key)) {
      result[key] = obj[key]
    }
  }
  return result
}

export function compactObject<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => !isNil(value)),
  ) as Partial<T>
}

/**
 * Creates an object composed of keys generated from the results of running each element of collection through iteratee.
 * The corresponding value of each key is the last element responsible for generating the key.
 *
 * @param collection - The collection to iterate over
 * @param iteratee - The iteratee to transform keys. Can be a function or a property name
 * @returns Returns the composed aggregate object
 */
export function keyBy<T>(
  collection: T[] | null | undefined,
  iteratee: ((value: T) => string | number) | keyof T,
): Record<string, T> {
  return (
    collection?.reduce<Record<string, T>>((acc, item) => {
      if (item === undefined || item === null) {
        return acc
      }

      const key =
        typeof iteratee === 'function' ? iteratee(item) : String(item[iteratee])
      acc[key] = item
      return acc
    }, {}) || {}
  )
}

/**
 * Creates an object composed of the own enumerable properties of object that predicate doesn't return truthy for.
 * The predicate is invoked with two arguments: (value, key).
 * If predicate is a string, it omits the property with that key name.
 *
 * @param object - The source object
 * @param predicate - The function invoked per property or a string key to omit
 * @returns Returns the new object
 */
export function omitBy<T extends object>(
  object: T,
  predicate: ((value: T[keyof T], key: keyof T) => boolean) | string,
): Partial<T> {
  const result = {} as Partial<T>

  for (const key in object) {
    if (Object.hasOwn(object, key)) {
      const value = object[key]

      // If predicate is a string, check if key matches
      if (typeof predicate === 'string') {
        // eslint-disable-next-line max-depth
        if (key !== predicate) {
          result[key] = value
        }
      } else if (typeof predicate === 'function') {
        // If predicate is a function, use it
        // eslint-disable-next-line max-depth
        if (!predicate(value, key)) {
          result[key] = value
        }
      }
    }
  }

  return result
}
